import { useCallback, useEffect, useRef, useState } from 'react';
import { useSession } from '../auth/session.js';
import { SignatureEditor } from '../components/SignatureModals.js';
import { syncSignature } from '../lib/signature.js';
import './usersModal.css';

/**
 * Provider Profile (PROVIDER_PROFILE_09-30-V14, ehr#60): the page ships as
 * /provider-profile.html and opens full screen over Users & Profile from a user's name or
 * pencil. It talks back with postMessage: 'saved' (reload the team), 'close', and
 * 'editSignature' (its "Edit signature" buttons).
 *
 * ehr#524: your signature lives on your own profile (About > Signature; it was Forms Hub >
 * My Signature). Edit signature opens the signature editor right here, over the profile, and
 * the profile is told when it closes so it shows the new one.
 */
export function ProviderProfileFrame({
  userId,
  onClose,
  onSaved,
  editSignature = false,
  onSignatureClosed,
}: {
  userId: string;
  onClose: () => void;
  onSaved: () => void;
  /** ehr#524: open with the signature editor up (the /?signature=edit link). */
  editSignature?: boolean;
  /** ehr#524: the editor closed (App drops the one-shot ?signature=edit). */
  onSignatureClosed?: () => void;
}) {
  const { user, setTimeFormat, setTheme } = useSession();
  const userKey = user?.id ?? user?.email ?? 'me';
  const frameRef = useRef<HTMLIFrameElement>(null);
  // ehr#524: 'off' | 'syncing' (this device lines up with the server first) | 'open'.
  const [sig, setSig] = useState<'off' | 'syncing' | 'open'>(editSignature ? 'syncing' : 'off');
  // The profile scrolls inside its own frame; lock the EHR page underneath so only one
  // scrollbar shows (ehr#60 follow-up).
  useEffect(() => {
    const els = [document.documentElement, document.body];
    const prev = els.map((el) => el.style.overflow);
    els.forEach((el) => (el.style.overflow = 'hidden'));
    return () => els.forEach((el, i) => (el.style.overflow = prev[i] ?? ''));
  }, []);

  // ehr#524: tell the profile page something (show a toast; the signature may have changed).
  const tellPage = useCallback((action: string, extra?: Record<string, unknown>) => {
    try {
      frameRef.current?.contentWindow?.postMessage(
        { type: 'credify:provider-profile-host', action, ...extra },
        window.location.origin,
      );
    } catch {
      /* the page went away: nothing to tell */
    }
  }, []);

  useEffect(() => {
    if (sig !== 'syncing') return;
    let live = true;
    // ehr#278: this device and the server agree on the saved signature before the editor reads it.
    void syncSignature(userKey).finally(() => {
      if (live) setSig('open');
    });
    return () => {
      live = false;
    };
  }, [sig, userKey]);

  const closeSignature = useCallback(() => {
    setSig('off');
    tellPage('signatureChanged');
    onSignatureClosed?.();
  }, [tellPage, onSignatureClosed]);

  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const d = e.data as { type?: string; action?: string; value?: string } | null;
      if (!d || d.type !== 'credify:provider-profile') return;
      if (d.action === 'saved') onSaved();
      if (d.action === 'close') onClose();
      // ehr#524: your own signature only (the page shows the button on your own profile alone).
      if (d.action === 'editSignature' && userId === user?.id)
        setSig((s) => (s === 'off' ? 'syncing' : s));
      // ehr#98: your time format, picked on your own profile, applies at once.
      if (d.action === 'timeFormat' && (d.value === '12h' || d.value === '24h'))
        setTimeFormat(d.value);
      // ehr#420: so does your theme
      if (
        d.action === 'theme' &&
        (d.value === 'light' || d.value === 'dark' || d.value === 'system')
      )
        setTheme(d.value);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [onClose, onSaved, setTimeFormat, setTheme, userId, user?.id]);

  return (
    <div className="pp-frame-ov" role="dialog" aria-modal="true" aria-label="Provider profile">
      <iframe
        ref={frameRef}
        className="pp-frame"
        title="Provider profile"
        src={`/provider-profile.html?user=${encodeURIComponent(userId)}${editSignature ? '&focus=signature' : ''}`}
      />
      {sig === 'open' && (
        <SignatureEditor
          userKey={userKey}
          context="settings"
          onClose={closeSignature}
          onToast={(text) => tellPage('toast', { text })}
        />
      )}
    </div>
  );
}
